var _TableUtils;
function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { Type as FilterType, Operator as FilterOperator } from '@deephaven/filters';
import Log from '@deephaven/log';
import { assertNotNull, bindAllMethods, PromiseUtils, removeNullAndUndefined, TextUtils, TimeoutError } from '@deephaven/utils';
import DateUtils from "./DateUtils.js";
import { createValueFilter } from "./FilterUtils.js";
import { getSize } from "./ViewportDataUtils.js";
var log = Log.module('TableUtils');

// Sort descriptor loosely matching the shape of dh.Sort

export function isSortDirection(value) {
  return value === TableUtils.sortDirection.ascending || value === TableUtils.sortDirection.descending || value === TableUtils.sortDirection.reverse || value === TableUtils.sortDirection.none;
}

/** Utility class to provide some functions for working with tables */
export class TableUtils {
  /**
   * Copy a given table and apply filters.
   * @param maybeTable Table to copy and apply filters to
   * @param filterFactories Filter condition factories to apply
   * @returns A derived, filtered table
   */
  static copyTableAndApplyFilters(maybeTable) {
    var _arguments = arguments;
    return _asyncToGenerator(function* () {
      if (maybeTable == null) {
        return null;
      }
      var derivedTable = yield maybeTable.copy();
      for (var _len = _arguments.length, filterFactories = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        filterFactories[_key - 1] = _arguments[_key];
      }
      derivedTable.applyFilter(removeNullAndUndefined(...filterFactories.map(f => f(derivedTable))));
      return derivedTable;
    })();
  }

  /**
   * Executes a callback on a given table and returns a Promise that will resolve
   * the next time a particular event type fires on the table.
   * @param exec Callback function to execute.
   * @param table Table that gets passed to the `exec` function and that is
   * subscribed to for a given `eventType`.
   * @param eventType The event type to listen for.
   * @param timeout If the event doesn't fire within the timeout, the returned
   * Promise will be rejected.
   * @returns a Promise to the original table that resolves on next `eventType`
   * event
   */

  static getSortIndex(sort, columnName) {
    for (var i = 0; i < sort.length; i += 1) {
      var _s$column;
      var s = sort[i];
      if (((_s$column = s.column) === null || _s$column === void 0 ? void 0 : _s$column.name) === columnName) {
        return i;
      }
    }
    return null;
  }

  /**
   * @param tableSort The sorts from the table to get the sort from
   * @param columnName The name of the column to get the sort for
   * @returns The sort for the column, or null if it's not sorted
   */
  static getSortForColumn(tableSort, columnName) {
    var sortIndex = TableUtils.getSortIndex(tableSort, columnName);
    if (sortIndex != null) {
      return tableSort[sortIndex];
    }
    return null;
  }
  static getFilterText(filter) {
    if (filter) {
      return filter.toString();
    }
    return null;
  }

  /** Return the valid filter types for the column */
  static getFilterTypes(columnType) {
    if (TableUtils.isBooleanType(columnType)) {
      return [FilterType.isTrue, FilterType.isFalse, FilterType.isNull];
    }
    if (TableUtils.isCharType(columnType) || TableUtils.isNumberType(columnType) || TableUtils.isDateType(columnType)) {
      return [FilterType.eq, FilterType.notEq, FilterType.greaterThan, FilterType.greaterThanOrEqualTo, FilterType.lessThan, FilterType.lessThanOrEqualTo];
    }
    if (TableUtils.isTextType(columnType)) {
      return [FilterType.eq, FilterType.eqIgnoreCase, FilterType.notEq, FilterType.notEqIgnoreCase, FilterType.contains, FilterType.notContains, FilterType.startsWith, FilterType.endsWith];
    }
    return [];
  }
  static getNextSort(columns, sorts, columnIndex) {
    var column = columns[columnIndex];
    if (column == null) {
      return null;
    }
    var sort = TableUtils.getSortForColumn(sorts, column.name);
    if (sort === null) {
      return this.makeColumnSort(columns, columnIndex, TableUtils.sortDirection.ascending, false);
    }
    if (sort.direction === TableUtils.sortDirection.ascending) {
      return this.makeColumnSort(columns, columnIndex, TableUtils.sortDirection.descending, false);
    }
    return null;
  }
  static makeColumnSort(columns, columnIndex, direction, isAbs) {
    var column = columns[columnIndex];
    if (column == null) {
      return null;
    }
    if (direction === TableUtils.sortDirection.none) {
      return null;
    }
    return {
      column: {
        name: column.name,
        type: column.type
      },
      isAbs,
      direction
    };
  }

  /**
   * Toggles the sort for the specified column
   * @param sorts The current sorts from IrisGrid.state
   * @param columns The columns to apply the sort to
   * @param columnIndex The column index to apply the sort to
   * @param addToExisting Add this sort to the existing sort
   */
  static toggleSortForColumn(sorts, columns, columnIndex) {
    var addToExisting = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
    if (columns[columnIndex] == null) {
      return [];
    }
    var newSort = TableUtils.getNextSort(columns, sorts, columnIndex);
    return TableUtils.setSortForColumn(sorts, columns[columnIndex].name, newSort, addToExisting);
  }
  static sortColumn(sorts, columns, modelColumn, direction, isAbs, addToExisting) {
    if (modelColumn < 0 || modelColumn >= columns.length) {
      return [];
    }
    var newSort = TableUtils.makeColumnSort(columns, modelColumn, direction, isAbs);
    return TableUtils.setSortForColumn(sorts, columns[modelColumn].name, newSort, addToExisting);
  }

  /**
   * Sets the sort for the given column *and* removes any reverses
   * @param tableSort The current sorts from IrisGrid.state
   * @param columnName The column name to apply the sort to
   * @param sort The sort object to add
   * @param addToExisting Add this sort to the existing sort
   * @returns Returns the modified array of sorts - removing reverses
   */
  static setSortForColumn(tableSort, columnName, sort) {
    var addToExisting = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
    var sortIndex = TableUtils.getSortIndex(tableSort, columnName);
    var sorts = [];
    if (addToExisting) {
      sorts = sorts.concat(tableSort.filter(_ref => {
        var direction = _ref.direction;
        return direction !== TableUtils.sortDirection.reverse;
      }));
      if (sortIndex !== null) {
        sorts.splice(sortIndex, 1);
      }
    }
    if (sort !== null) {
      sorts.push(sort);
    }
    return sorts;
  }
  static getNormalizedType(columnType) {
    switch (columnType) {
      case 'boolean':
      case 'java.lang.Boolean':
      case TableUtils.dataType.BOOLEAN:
        return TableUtils.dataType.BOOLEAN;
      case 'char':
      case 'java.lang.Character':
      case TableUtils.dataType.CHAR:
        return TableUtils.dataType.CHAR;
      case 'java.lang.String':
      case TableUtils.dataType.STRING:
        return TableUtils.dataType.STRING;
      case 'io.deephaven.db.tables.utils.DBDateTime':
      case 'io.deephaven.time.DateTime':
      case 'com.illumon.iris.db.tables.utils.DBDateTime':
      case 'java.time.Instant':
      case 'java.time.ZonedDateTime':
      case TableUtils.dataType.DATETIME:
        return TableUtils.dataType.DATETIME;
      case 'double':
      case 'java.lang.Double':
      case 'float':
      case 'java.lang.Float':
      case 'java.math.BigDecimal':
      case TableUtils.dataType.DECIMAL:
        return TableUtils.dataType.DECIMAL;
      case 'int':
      case 'java.lang.Integer':
      case 'long':
      case 'java.lang.Long':
      case 'short':
      case 'java.lang.Short':
      case 'byte':
      case 'java.lang.Byte':
      case 'java.math.BigInteger':
      case TableUtils.dataType.INT:
        return TableUtils.dataType.INT;
      default:
        return TableUtils.dataType.UNKNOWN;
    }
  }
  static isLongType(columnType) {
    switch (columnType) {
      case 'long':
      case 'java.lang.Long':
        return true;
      default:
        return false;
    }
  }
  static isDateType(columnType) {
    switch (columnType) {
      case 'io.deephaven.db.tables.utils.DBDateTime':
      case 'io.deephaven.time.DateTime':
      case 'java.time.Instant':
      case 'java.time.ZonedDateTime':
      case 'com.illumon.iris.db.tables.utils.DBDateTime':
        return true;
      default:
        return false;
    }
  }
  static isNumberType(columnType) {
    return TableUtils.isIntegerType(columnType) || TableUtils.isDecimalType(columnType);
  }
  static isIntegerType(columnType) {
    switch (columnType) {
      case 'int':
      case 'java.lang.Integer':
      case 'java.math.BigInteger':
      case 'long':
      case 'java.lang.Long':
      case 'short':
      case 'java.lang.Short':
      case 'byte':
      case 'java.lang.Byte':
        return true;
      default:
        return false;
    }
  }
  static isDecimalType(columnType) {
    switch (columnType) {
      case 'double':
      case 'java.lang.Double':
      case 'java.math.BigDecimal':
      case 'float':
      case 'java.lang.Float':
        return true;
      default:
        return false;
    }
  }
  static isBigDecimalType(columnType) {
    switch (columnType) {
      case 'java.math.BigDecimal':
        return true;
      default:
        return false;
    }
  }
  static isBigIntegerType(columnType) {
    switch (columnType) {
      case 'java.math.BigInteger':
        return true;
      default:
        return false;
    }
  }
  static isBooleanType(columnType) {
    switch (columnType) {
      case 'boolean':
      case 'java.lang.Boolean':
        return true;
      default:
        return false;
    }
  }
  static isCharType(columnType) {
    switch (columnType) {
      case 'char':
      case 'java.lang.Character':
        return true;
      default:
        return false;
    }
  }
  static isStringType(columnType) {
    switch (columnType) {
      case 'java.lang.String':
        return true;
      default:
        return false;
    }
  }
  static isTextType(columnType) {
    return this.isStringType(columnType) || this.isCharType(columnType);
  }
  static isArrayType(columnType) {
    return columnType.includes('[]');
  }

  /**
   * Get base column type
   * @param columnType Column type
   * @returns Element type for array columns, original type for non-array columns
   */
  static getBaseType(columnType) {
    return columnType.split('[]')[0];
  }

  /**
   * Check if the column types are compatible
   * @param type1 Column type to check
   * @param type2 Column type to check
   * @returns True, if types are compatible
   */
  static isCompatibleType(type1, type2) {
    return TableUtils.getNormalizedType(type1) === TableUtils.getNormalizedType(type2);
  }

  /**
   * Adds quotes to a value if they're not already added
   * @param value Value to add quotes around
   */
  static quoteValue(value) {
    if (value.length >= 2 && (value.charAt(0) === '"' && value.charAt(value.length - 1) === '"' || value.charAt(0) === "'" && value.charAt(value.length - 1) === "'")) {
      return value;
    }
    return "\"".concat(value, "\"");
  }
  static isRangeOperation(operation) {
    switch (operation) {
      case '<':
      case '<=':
      case '=<':
      case '>':
      case '>=':
      case '=>':
        return true;
      default:
        return false;
    }
  }

  /**
   * @param filter The column filter to apply the range operation to
   * @param operation The range operation to run
   * @param value The value to use for the operation
   * @returns The condition with the specified operation
   */
  static makeRangeFilterWithOperation(filter, operation, value) {
    switch (operation) {
      case '=':
        return filter.eq(value);
      case '<':
        return filter.lessThan(value);
      case '<=':
      case '=<':
        return filter.lessThanOrEqualTo(value);
      case '>':
        return filter.greaterThan(value);
      case '>=':
      case '=>':
        return filter.greaterThanOrEqualTo(value);
      case '!=':
      case '!':
        return filter.notEq(value);
      default:
        return null;
    }
  }

  /**
   * Wraps a table promise in a cancelable promise that will close the table if the promise is cancelled.
   * Use in a component that loads a table, and call cancel when unmounting.
   * @param table The table promise to wrap
   */
  static makeCancelableTablePromise(table) {
    return PromiseUtils.makeCancelable(table, resolved => {
      resolved.close();
    });
  }

  /**
   * Make a cancelable promise for a one-shot table event with a timeout.
   * @param table Table to listen for events on
   * @param eventName Event to listen for
   * @param timeout Event timeout in milliseconds, defaults to 0
   * @param matcher Optional function to determine if the promise can be resolved or stays pending
   * @deprecated Use `makeCancelableEventPromise` instead.
   * @returns Resolves with the event data
   */
  static makeCancelableTableEventPromise(table, eventName) {
    var timeout = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
    var matcher = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : null;
    return TableUtils.makeCancelableEventPromise(table, eventName, {
      timeout,
      matcher: matcher !== null && matcher !== void 0 ? matcher : undefined
    });
  }

  /**
   * Make a cancelable promise for a one-shot event with a timeout.
   * @param emitter The object emitting the event
   * @param eventName Event to listen for
   * @param options Optional options for listening
   * @param options.timeout Event timeout in milliseconds, defaults to 0
   * @param options.matcher Optional function to determine if the promise can be resolved or stays pending
   * @returns Resolves with the event data
   */
  static makeCancelableEventPromise(emitter, eventName) {
    var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    var _options$timeout = options.timeout,
      timeout = _options$timeout === void 0 ? 0 : _options$timeout,
      matcher = options.matcher;
    var eventCleanup;
    var timeoutId;
    var isPending = true;
    var wrappedPromise = new Promise((resolve, reject) => {
      timeoutId = setTimeout(() => {
        eventCleanup();
        isPending = false;
        reject(new TimeoutError("Event \"".concat(eventName, "\" timed out.")));
      }, timeout);
      eventCleanup = emitter.addEventListener(eventName, event => {
        if (matcher != null && !matcher(event)) {
          log.debug2('Event triggered, but matcher returned false.');
          return;
        }
        log.debug2('Event triggered, resolving.');
        eventCleanup();
        clearTimeout(timeoutId);
        isPending = false;
        resolve(event);
      });
    });
    wrappedPromise.cancel = () => {
      if (isPending) {
        log.debug2('Pending promise cleanup.');
        eventCleanup();
        clearTimeout(timeoutId);
        isPending = false;
        return;
      }
      log.debug2('Ignoring non-pending promise cancel.');
    };
    return wrappedPromise;
  }
  static removeCommas(value) {
    return value.replace(/[\s|,]/g, '');
  }
  static makeBooleanValue(text) {
    var allowEmpty = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    if (text === '' && allowEmpty) {
      return null;
    }
    switch (text === null || text === void 0 ? void 0 : text.toLowerCase()) {
      case 'null':
        return null;
      case '0':
      case 'f':
      case 'fa':
      case 'fal':
      case 'fals':
      case 'false':
      case 'n':
      case 'no':
        return false;
      case '1':
      case 't':
      case 'tr':
      case 'tru':
      case 'true':
      case 'y':
      case 'ye':
      case 'yes':
        return true;
      default:
        throw new Error("Invalid boolean '".concat(text, "'"));
    }
  }
  static makeNumberValue(text) {
    if (text === 'null' || text === '') {
      return null;
    }
    var cleanText = text.toLowerCase().trim();
    if (cleanText === '∞' || cleanText === 'infinity' || cleanText === 'inf') {
      return Number.POSITIVE_INFINITY;
    }
    if (cleanText === '-∞' || cleanText === '-infinity' || cleanText === '-inf') {
      return Number.NEGATIVE_INFINITY;
    }
    var numberText = TableUtils.removeCommas(cleanText);
    if (TableUtils.NUMBER_REGEX.test(numberText)) {
      return parseFloat(numberText);
    }
    throw new Error("Invalid number '".concat(text, "'"));
  }
  static getFilterOperatorString(operation) {
    switch (operation) {
      case FilterType.eq:
        return '=';
      case FilterType.notEq:
        return '!=';
      case FilterType.greaterThan:
        return '>';
      case FilterType.greaterThanOrEqualTo:
        return '>=';
      case FilterType.lessThan:
        return '<';
      case FilterType.lessThanOrEqualTo:
        return '<=';
      case FilterType.contains:
        return '~';
      case FilterType.notContains:
        return '!~';
      default:
        throw new Error("Unexpected filter type ".concat(operation));
    }
  }
  static isPartitionedTable(table) {
    return table != null && table.getMergedTable !== undefined && table.getKeyTable !== undefined && table.getKeys !== undefined;
  }
  static isTreeTable(table) {
    return table != null && table.expand !== undefined && table.collapse !== undefined;
  }

  /**
   * Copies the provided array, sorts by column name case insensitive, and returns the sorted array.
   * @param columns The columns to sort
   * @param isAscending Whether to sort ascending
   */
  static sortColumns(columns) {
    var isAscending = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    return [...columns].sort((a, b) => {
      var aName = a.name.toUpperCase();
      var bName = b.name.toUpperCase();
      return TextUtils.sort(aName, bName, isAscending);
    });
  }
  constructor(dh) {
    _defineProperty(this, "dh", void 0);
    this.dh = dh;
    bindAllMethods(this);
  }

  /**
   * Create a table containing a distinct list of values for given column name and
   * applies the given sort direction.
   * @param table Source table to derive table from
   * @param columnName Column to dermine distinct values
   * @param sortDirection Direction to sort
   * @param filterConditionFactories Optional filters to apply. Note that these
   * will be applied before the `selectCall` in case we need to base the filtering
   * on columns other than the distinct value column
   */
  createDistinctSortedColumnTable(table, columnName, sortDirection) {
    var _arguments2 = arguments,
      _this = this;
    return _asyncToGenerator(function* () {
      if (table == null) {
        return null;
      }
      var sourceTable = table;

      // Applying filters before `selectDistinct` so that we still have access to
      // all columns. Also prevents a filter applied to the final table from
      // clearing this filter.
      for (var _len2 = _arguments2.length, filterConditionFactories = new Array(_len2 > 3 ? _len2 - 3 : 0), _key2 = 3; _key2 < _len2; _key2++) {
        filterConditionFactories[_key2 - 3] = _arguments2[_key2];
      }
      if (filterConditionFactories.length > 0) {
        sourceTable = yield table.copy();
        yield sourceTable.applyFilter(removeNullAndUndefined(...filterConditionFactories.map(f => f(sourceTable))));
      }
      var column = sourceTable.findColumn(columnName);
      var distinctTable = yield sourceTable.selectDistinct([column]);

      // If we copied the table, dispose the copy. Original is managed outside
      // of this function
      if (sourceTable !== table) {
        sourceTable.close();
      }
      var distinctAscColSort = distinctTable.findColumn(columnName).sort()[sortDirection]();
      return _this.applySort(distinctTable, [distinctAscColSort]);
    })();
  }

  /**
   * Check if any columns contain a given value.
   * @param table Table to search for values
   * @param columnNames Column names to search
   * @param value Value to search for
   * @param isCaseSensitive Whether the value check is case sensitive
   */
  doesColumnValueExist(table, columnNames, value, isCaseSensitive) {
    var _this2 = this;
    return _asyncToGenerator(function* () {
      if (table == null) {
        return null;
      }
      var filterConditionFactory = createValueFilter(_this2, columnNames, value, isCaseSensitive ? 'eq' : 'eqIgnoreCase');
      var tableCopy = yield table.copy();
      yield _this2.applyFilter(tableCopy, removeNullAndUndefined(filterConditionFactory(tableCopy)));
      var size = getSize(tableCopy);
      tableCopy.close();
      return size > 0;
    })();
  }

  /**
   * Get the `dh.ValueType` corresponding to the given `dh.Column.type` value.
   * @param columnType The column type to get the value type for
   * @returns The `dh.ValueType` corresponding to the column type
   */
  getValueType(columnType) {
    if (columnType == null) {
      return this.dh.ValueType.STRING;
    }
    var columnDataType = TableUtils.getNormalizedType(columnType);
    switch (columnDataType) {
      case TableUtils.dataType.BOOLEAN:
        return this.dh.ValueType.BOOLEAN;
      case TableUtils.dataType.CHAR:
      case TableUtils.dataType.STRING:
        return this.dh.ValueType.STRING;
      case TableUtils.dataType.DATETIME:
        return this.dh.ValueType.DATETIME;
      case TableUtils.dataType.DECIMAL:
      case TableUtils.dataType.INT:
        if (TableUtils.isBigDecimalType(columnType) || TableUtils.isBigIntegerType(columnType)) {
          return this.dh.ValueType.STRING;
        }
        return this.dh.ValueType.NUMBER;
      default:
        return this.dh.ValueType.STRING;
    }
  }

  /**
   * Create filter with the provided column and text. Handles multiple filters joined with && or ||
   * @param column The column to set the filter on
   * @param text The text string to create the filter from
   * @param timeZone The time zone to make this value in if it is a date type. E.g. America/New_York
   * @returns Returns the created filter, null if text could not be parsed
   */
  makeQuickFilter(column, text, timeZone) {
    var orComponents = text.split('||');
    var orFilter = null;
    for (var i = 0; i < orComponents.length; i += 1) {
      var orComponent = orComponents[i];
      var andComponents = orComponent.split('&&');
      var andFilter = null;
      for (var j = 0; j < andComponents.length; j += 1) {
        var andComponent = andComponents[j].trim();
        if (andComponent.length > 0) {
          var filter = this.makeQuickFilterFromComponent(column, andComponent, timeZone);
          if (filter) {
            if (andFilter) {
              andFilter = andFilter.and(filter);
            } else {
              andFilter = filter;
            }
          } else {
            throw new Error("Unable to parse quick filter from text ".concat(text));
          }
        }
      }
      if (orFilter && andFilter) {
        orFilter = orFilter.or(andFilter);
      } else {
        orFilter = andFilter;
      }
    }
    return orFilter;
  }

  /**
   * Create filter with the provided column and text of one component (no multiple conditions)
   * @param column The column to set the filter on
   * @param text The text string to create the filter from
   * @param timeZone The time zone to make this filter in if it is a date type. E.g. America/New_York
   * @returns Returns the created filter, null if text could not be parsed
   */
  makeQuickFilterFromComponent(column, text, timeZone) {
    var type = column.type;
    if (TableUtils.isNumberType(type)) {
      return this.makeQuickNumberFilter(column, text);
    }
    if (TableUtils.isBooleanType(type)) {
      return this.makeQuickBooleanFilter(column, text);
    }
    if (timeZone != null && TableUtils.isDateType(type)) {
      return this.makeQuickDateFilter(column, text, timeZone);
    }
    if (TableUtils.isCharType(type)) {
      return this.makeQuickCharFilter(column, text);
    }
    return this.makeQuickTextFilter(column, text);
  }
  makeQuickNumberFilter(column, text) {
    var columnFilter = column.filter();
    var dh = this.dh;
    var filter = null;
    var regex = /\s*(>=|<=|=>|=<|>|<|!=|=|!)?(\s*-\s*)?(\s*\d*(?:,\d{3})*(?:\.\d*)?\s*)?(null|nan|infinity|inf|\u221E)?(.*)/i;
    var result = regex.exec(text);
    var operation = null;
    var negativeSign = null;
    var value = null;
    var abnormalValue = null; // includes nan, null and infinity(positive & negative)
    var overflow = null;
    if (result !== null && result.length > 3) {
      var _result = _slicedToArray(result, 6);
      operation = _result[1];
      negativeSign = _result[2];
      value = _result[3];
      abnormalValue = _result[4];
      overflow = _result[5];
    }
    if (overflow != null && overflow.trim().length > 0) {
      // Some bad characters after the number, bail out!
      return null;
    }
    if (operation == null) {
      operation = '=';
    }
    if (abnormalValue != null) {
      if (!(operation === '=' || operation === '!' || operation === '!=')) {
        // only equal and not equal operations are supported for abnormal value filter
        return null;
      }
      abnormalValue = abnormalValue.trim().toLowerCase();
      switch (abnormalValue) {
        case 'null':
          filter = columnFilter.isNull();
          break;
        case 'nan':
          filter = dh.FilterCondition.invoke('isNaN', columnFilter);
          break;
        case 'infinity':
        case 'inf':
        case '\u221E':
          if (negativeSign != null) {
            filter = dh.FilterCondition.invoke('isInf', columnFilter).and(columnFilter.lessThan(dh.FilterValue.ofNumber(0)));
          } else {
            filter = dh.FilterCondition.invoke('isInf', columnFilter).and(columnFilter.greaterThan(dh.FilterValue.ofNumber(0)));
          }
          break;
        default:
          break;
      }
      if (filter !== null && (operation === '!' || operation === '!=')) {
        filter = filter.not();
      }
      return filter;
    }
    if (value == null) {
      return null;
    }
    value = TableUtils.removeCommas(value);
    if (TableUtils.isLongType(column.type)) {
      try {
        value = dh.FilterValue.ofNumber(dh.LongWrapper.ofString("".concat(negativeSign != null ? '-' : '').concat(value)));
      } catch (error) {
        log.warn('Unable to create long filter', error);
        return null;
      }
    } else {
      value = parseFloat(value);
      if (value == null || Number.isNaN(value)) {
        return null;
      }
      value = dh.FilterValue.ofNumber(negativeSign != null ? 0 - value : value);
    }
    filter = column.filter();
    return TableUtils.makeRangeFilterWithOperation(filter, operation, value);
  }

  /**
   * Given a text string from a table, escape quick filter operators in string with \
   * ex. =test returns \=test, null returns \null
   * @param string quickfilter string to escape
   * @returns escaped string
   */
  static escapeQuickTextFilter(quickFilterText) {
    if (quickFilterText == null) return null;
    var regex = /^(!~|!=|~|=|!)?(.*)/;
    // starts with zero or more \ followed by and ending with null
    var nullRegex = /^\\*null$/;
    var result = regex.exec(quickFilterText);
    var operation = null;
    var value = null;
    if (result !== null && result.length > 2) {
      var _result2 = _slicedToArray(result, 3);
      operation = _result2[1];
      value = _result2[2];
    }
    if (operation != null) {
      return "\\".concat(operation).concat(value !== null && value !== void 0 ? value : '');
    }
    if (value != null && nullRegex.test(value.toLowerCase())) {
      // adds an extra escape character to matching value
      return "\\".concat(value);
    }
    if (value != null && value.startsWith('*')) {
      return "\\".concat(value);
    }
    if (value != null && value.endsWith('*') && !value.endsWith('\\*')) {
      value = value.substring(0, value.length - 1);
      return "".concat(value, "\\*");
    }
    return "".concat(operation !== null && operation !== void 0 ? operation : '').concat(value !== null && value !== void 0 ? value : "");
  }

  /**
   * Given an escaped quick filter, unescape the operators for giving it to the js api
   * ex. \=test returns =test, \null returns null
   * @param string quickfilter string to escape
   * @returns escaped string
   */
  static unescapeQuickTextFilter(quickFilterText) {
    var regex = /^(\\!~|\\!=|\\~|\\=|\\!)?(.*)/;
    // starts with zero or more \ followed by and ending with null
    var nullRegex = /^\\*null$/;
    var result = regex.exec(quickFilterText);
    var operation = null;
    var value = null;
    if (result !== null && result.length > 2) {
      var _result3 = _slicedToArray(result, 3);
      operation = _result3[1];
      value = _result3[2];
    }
    if (operation != null) {
      operation = operation.replace('\\', '');
    }
    if (value != null && nullRegex.test(value.toLowerCase())) {
      // removes the first occurance of the backslash
      value = value.replace('\\', '');
    }
    if (operation == null && value != null && value.startsWith('\\*')) {
      value = value.substring(1);
    }
    if (operation == null && value != null && value.endsWith('\\*')) {
      value = value.substring(0, value.length - 2);
      return "".concat(value, "*");
    }
    return "".concat(operation !== null && operation !== void 0 ? operation : '').concat(value !== null && value !== void 0 ? value : "");
  }
  makeQuickTextFilter(column, text) {
    var dh = this.dh;
    var cleanText = "".concat(text).trim();
    var regex = /^(!~|!=|~|=|!)?(.*)/;
    var result = regex.exec(cleanText);
    var operation = null;
    var value = null;
    if (result !== null && result.length > 2) {
      var _result4 = _slicedToArray(result, 3);
      operation = _result4[1];
      value = _result4[2];
      if (value != null) {
        value = value.trim();
      }
    }
    if (value == null) {
      return null;
    }

    // allow empty strings, but only for explicit equal and not equal
    if (value.length === 0 && !(operation === '=' || operation === '!=')) {
      return null;
    }

    // no operation is treated as an implicit equals
    if (operation == null) {
      operation = '=';
    }
    var filter = column.filter();
    if (value.toLowerCase() === 'null') {
      // Null is a special case!
      switch (operation) {
        case '=':
          return filter.isNull();
        case '!=':
        case '!':
          return filter.isNull().not();
        default:
        // For all other operations, treat null as a string value
      }
    }
    var prefix = null;
    var suffix = null;
    if (value.startsWith('*')) {
      prefix = '*';
      value = value.substring(1);
    } else if (value.endsWith('*') && !value.endsWith('\\*')) {
      suffix = '*';
      value = value.substring(0, value.length - 1);
    }

    // unescape any escaped operators to allow search for literal operators
    value = TableUtils.unescapeQuickTextFilter(value);
    switch (operation) {
      case '~':
        {
          return filter.isNull().not().and(filter.invoke('matches', dh.FilterValue.ofString("(?s)(?i).*\\Q".concat(value, "\\E.*"))));
        }
      case '!~':
        return filter.isNull().or(filter.invoke('matches', dh.FilterValue.ofString("(?s)(?i).*\\Q".concat(value, "\\E.*"))).not());
      case '!=':
        if (prefix === '*') {
          // Does not end with
          return filter.isNull().or(filter.invoke('matches', dh.FilterValue.ofString("(?s)(?i).*\\Q".concat(value, "\\E$"))).not());
        }
        if (suffix === '*') {
          // Does not start with
          return filter.isNull().or(filter.invoke('matches', dh.FilterValue.ofString("(?s)(?i)^\\Q".concat(value, "\\E.*"))).not());
        }
        return filter.notEqIgnoreCase(dh.FilterValue.ofString(value.toLowerCase()));
      case '=':
        if (prefix === '*') {
          // Ends with
          return filter.isNull().not().and(filter.invoke('matches', dh.FilterValue.ofString("(?s)(?i).*\\Q".concat(value, "\\E$"))));
        }
        if (suffix === '*') {
          // Starts with
          return filter.isNull().not().and(filter.invoke('matches', dh.FilterValue.ofString("(?s)(?i)^\\Q".concat(value, "\\E.*"))));
        }
        return filter.eqIgnoreCase(dh.FilterValue.ofString(value.toLowerCase()));
      default:
        break;
    }
    return null;
  }

  // eslint-disable-next-line class-methods-use-this
  makeQuickBooleanFilter(column, text) {
    var regex = /^(!=|=|!)?(.*)/;
    var result = regex.exec("".concat(text).trim());
    if (result === null) {
      return null;
    }
    var _result5 = _slicedToArray(result, 3),
      operation = _result5[1],
      value = _result5[2];
    var notEqual = operation === '!' || operation === '!=';
    var cleanValue = value.trim().toLowerCase();
    var filter = column.filter();
    try {
      var boolValue = TableUtils.makeBooleanValue(cleanValue);
      if (boolValue != null && boolValue) {
        filter = filter.isTrue();
      } else if (boolValue === null) {
        filter = filter.isNull();
      } else {
        filter = filter.isFalse();
      }
      return notEqual ? filter.not() : filter;
    } catch (e) {
      return null;
    }
  }

  /**
   * Builds a date filter parsed from the text string which may or may not include an operator.
   * @param column The column to build the filter from, with or without a leading operator.
   * @param text The date string text to parse.
   * @param timeZone The time zone to make this filter in if it is a date type. E.g. America/New_York
   */
  makeQuickDateFilter(column, text, timeZone) {
    var cleanText = text.trim();
    var regex = /\s*(>=|<=|=>|=<|>|<|!=|!|=)?(.*)/;
    var result = regex.exec(cleanText);
    if (result == null || result.length <= 2) {
      throw new Error("Unable to parse date filter: ".concat(text));
    }
    var operation = null;
    var dateText = null;
    var _result6 = _slicedToArray(result, 3);
    operation = _result6[1];
    dateText = _result6[2];
    var filterOperation = FilterType.eq;
    switch (operation) {
      case '<':
        filterOperation = FilterType.lessThan;
        break;
      case '<=':
      case '=<':
        filterOperation = FilterType.lessThanOrEqualTo;
        break;
      case '>':
        filterOperation = FilterType.greaterThan;
        break;
      case '>=':
      case '=>':
        filterOperation = FilterType.greaterThanOrEqualTo;
        break;
      case '!=':
      case '!':
        filterOperation = FilterType.notEq;
        break;
      case '=':
      case '==':
      default:
        filterOperation = FilterType.eq;
        break;
    }
    return this.makeQuickDateFilterWithOperation(column, dateText, filterOperation, timeZone);
  }

  /**
   * Builds a date filter parsed from the text string with the provided filter.
   * @param column The column to build the filter from.
   * @param text The date string text to parse, without an operator.
   * @param operation The filter operation to use.
   * @param timeZone The time zone to make this filter with. E.g. America/New_York
   */
  makeQuickDateFilterWithOperation(column, text, operation, timeZone) {
    var dh = this.dh;
    var _DateUtils$parseDateR = DateUtils.parseDateRange(dh, text, timeZone),
      _DateUtils$parseDateR2 = _slicedToArray(_DateUtils$parseDateR, 2),
      startDate = _DateUtils$parseDateR2[0],
      endDate = _DateUtils$parseDateR2[1];
    var startValue = startDate != null ? dh.FilterValue.ofNumber(startDate) : null;
    var endValue = endDate != null ? dh.FilterValue.ofNumber(endDate) : null;
    var filter = column.filter();
    if (startValue == null) {
      return operation === FilterType.notEq ? filter.isNull().not() : filter.isNull();
    }
    switch (operation) {
      case FilterType.eq:
        {
          if (endValue != null) {
            var startFilter = filter.greaterThanOrEqualTo(startValue);
            var endFilter = filter.lessThan(endValue);
            return startFilter.and(endFilter);
          }
          return filter.eq(startValue);
        }
      case FilterType.lessThan:
        {
          return filter.lessThan(startValue);
        }
      case FilterType.lessThanOrEqualTo:
        {
          if (endValue != null) {
            return filter.lessThan(endValue);
          }
          return filter.lessThanOrEqualTo(startValue);
        }
      case FilterType.greaterThan:
        {
          if (endValue != null) {
            return filter.greaterThanOrEqualTo(endValue);
          }
          return filter.greaterThan(startValue);
        }
      case FilterType.greaterThanOrEqualTo:
        return filter.greaterThanOrEqualTo(startValue);
      case FilterType.notEq:
        {
          if (endValue != null) {
            var _startFilter = filter.lessThan(startValue);
            var _endFilter = filter.greaterThanOrEqualTo(endValue);
            return _startFilter.or(_endFilter);
          }
          return filter.notEq(startValue);
        }
      default:
        throw new Error("Invalid operator: ".concat(operation));
    }
  }
  makeQuickCharFilter(column, text) {
    var dh = this.dh;
    var cleanText = "".concat(text).trim();
    var regex = /^(>=|<=|=>|=<|>|<|!=|=|!)?(null|"."|'.'|.)?(.*)/;
    var result = regex.exec(cleanText);
    var operation = null;
    var value = null;
    var overflow = null;
    if (result !== null && result.length > 3) {
      var _result7 = _slicedToArray(result, 4);
      operation = _result7[1];
      value = _result7[2];
      overflow = _result7[3];
    }
    if (overflow != null && overflow.trim().length > 0) {
      // Some bad characters after the number, bail out!
      return null;
    }
    if (value == null || value.length === 0) {
      return null;
    }
    if (operation == null) {
      operation = '=';
    }
    var filter = column.filter();
    if (value.toLowerCase() === 'null') {
      // Null is a special case!
      switch (operation) {
        case '=':
          return filter.isNull();
        case '!=':
        case '!':
          return filter.isNull().not();
        default:
          return null;
      }
    }

    // We need to put quotes around range operations or else the API fails
    var filterValue = dh.FilterValue.ofString(TableUtils.isRangeOperation(operation) ? TableUtils.quoteValue(value) : value);
    return TableUtils.makeRangeFilterWithOperation(filter, operation, filterValue);
  }
  makeAdvancedFilter(column, options, timeZone) {
    var filterItems = options.filterItems,
      filterOperators = options.filterOperators,
      invertSelection = options.invertSelection,
      selectedValues = options.selectedValues;
    var filter = null;
    for (var i = 0; i < filterItems.length; i += 1) {
      var filterItem = filterItems[i];
      var selectedType = filterItem.selectedType,
        value = filterItem.value;
      if (selectedType != null && selectedType.length > 0 && value != null && value.length > 0) {
        try {
          var newFilter = this.makeAdvancedValueFilter(column, selectedType, value, timeZone);
          if (newFilter != null) {
            if (i === 0) {
              filter = newFilter;
            } else if (filter !== null && i - 1 < filterOperators.length) {
              var filterOperator = filterOperators[i - 1];
              if (filterOperator === FilterOperator.and) {
                filter = filter.and(newFilter);
              } else if (filterOperator === FilterOperator.or) {
                filter = filter.or(newFilter);
              } else {
                log.error('Unexpected filter operator', filterOperator, newFilter);
                filter = null;
                break;
              }
            }
          } else {
            log.debug2('Empty filter ignored for', selectedType, value);
          }
        } catch (err) {
          log.error('Unable to create filter', err);
          filter = null;
          break;
        }
      }
    }
    var selectValueFilter = this.makeSelectValueFilter(column, selectedValues, invertSelection);
    if (selectValueFilter != null) {
      if (filter != null) {
        filter = filter.and(selectValueFilter);
      } else {
        filter = selectValueFilter;
      }
    }
    return filter;
  }
  makeAdvancedValueFilter(column, operation, value, timeZone) {
    var dh = this.dh;
    if (TableUtils.isDateType(column.type)) {
      return this.makeQuickDateFilterWithOperation(column, value, operation, timeZone);
    }
    if (TableUtils.isNumberType(column.type) || TableUtils.isCharType(column.type)) {
      return this.makeQuickFilter(column, "".concat(TableUtils.getFilterOperatorString(operation)).concat(value));
    }
    var filterValue = this.makeFilterValue(column.type, value);
    var filter = column.filter();
    switch (operation) {
      case FilterType.eq:
        return filter.eq(filterValue);
      case FilterType.eqIgnoreCase:
        return filter.eqIgnoreCase(filterValue);
      case FilterType.notEq:
        return filter.notEq(filterValue);
      case FilterType.notEqIgnoreCase:
        return filter.notEqIgnoreCase(filterValue);
      case FilterType.greaterThan:
        return filter.greaterThan(filterValue);
      case FilterType.greaterThanOrEqualTo:
        return filter.greaterThanOrEqualTo(filterValue);
      case FilterType.lessThan:
        return filter.lessThan(filterValue);
      case FilterType.lessThanOrEqualTo:
        return filter.lessThanOrEqualTo(filterValue);
      case FilterType.isTrue:
        return filter.isTrue();
      case FilterType.isFalse:
        return filter.isFalse();
      case FilterType.isNull:
        return filter.isNull();
      case FilterType.contains:
        return filter.isNull().not().and(filter.invoke('matches', dh.FilterValue.ofString("(?s)(?i).*\\Q".concat(value, "\\E.*"))));
      case FilterType.notContains:
        return filter.isNull().or(filter.invoke('matches', dh.FilterValue.ofString("(?s)(?i).*\\Q".concat(value, "\\E.*"))).not());
      case FilterType.startsWith:
        return filter.isNull().not().and(filter.invoke('matches', dh.FilterValue.ofString("(?s)(?i)^\\Q".concat(value, "\\E.*"))));
      case FilterType.endsWith:
        return filter.isNull().not().and(filter.invoke('matches', dh.FilterValue.ofString("(?s)(?i).*\\Q".concat(value, "\\E$"))));
      case FilterType.in:
      case FilterType.inIgnoreCase:
      case FilterType.notIn:
      case FilterType.notInIgnoreCase:
      case FilterType.invoke:
      default:
        throw new Error("Unexpected filter operation: ".concat(operation));
    }
  }

  /**
   * Create a filter condition that can search a column by a given `searchText`
   * value.
   * @param column The column to search
   * @param searchText The text to search for
   * @param timeZone The time zone to make this filter in if it is a date type. E.g. America/New_York
   * @returns The filter condition that can be applied to the column
   */
  makeSearchTextFilter(column, searchText, timeZone) {
    var valueType = this.getValueType(column.type);
    try {
      if (valueType === this.dh.ValueType.BOOLEAN) {
        var maybeFilterCondition = this.makeQuickBooleanFilter(column, searchText);
        assertNotNull(maybeFilterCondition);
        return maybeFilterCondition;
      }
      if (valueType === this.dh.ValueType.DATETIME) {
        return this.makeQuickDateFilterWithOperation(column, DateUtils.trimDateTimeStringTimeZone(searchText), 'eq', timeZone);
      }
      if (valueType === this.dh.ValueType.NUMBER) {
        var _maybeFilterCondition = this.makeQuickNumberFilter(column, searchText);
        assertNotNull(_maybeFilterCondition);
        return _maybeFilterCondition;
      }

      // Treat big numbers as strings
      if (TableUtils.isBigDecimalType(column.type) || TableUtils.isBigIntegerType(column.type)) {
        return column.filter().eq(this.makeFilterValue(column.type, searchText));
      }
      return column.filter().containsIgnoreCase(this.makeFilterValue(column.type, searchText));
    } catch (_unused) {
      return this.makeNeverFilter(column);
    }
  }

  /**
   * Apply a filter to a table that won't match anything.
   * @table The table to apply the filter to
   * @columnName The name of the column to apploy the filter to
   * @param timeout Timeout before cancelling the promise that waits for the next
   * dh.Table.EVENT_FILTERCHANGED event
   * @returns a Promise to the Table that resolves after the next
   * dh.Table.EVENT_FILTERCHANGED event
   */
  applyNeverFilter(table, columnName) {
    var _arguments3 = arguments,
      _this3 = this;
    return _asyncToGenerator(function* () {
      var timeout = _arguments3.length > 2 && _arguments3[2] !== undefined ? _arguments3[2] : TableUtils.APPLY_TABLE_CHANGE_TIMEOUT_MS;
      if (table == null) {
        return null;
      }
      var column = table.findColumn(columnName);
      var filters = [_this3.makeNeverFilter(column)];
      yield _this3.applyFilter(table, filters, timeout);
      return table;
    })();
  }

  /**
   * Apply custom columns to a given table. Return a Promise that resolves with
   * the table once the dh.Table.EVENT_CUSTOMCOLUMNSCHANGED event has fired.
   * @param table The table to apply custom columns to.
   * @param columns The list of column expressions or definitions to apply.
   * @returns A Promise that will be resolved with the given table after the
   * columns are applied.
   */
  applyCustomColumns(table, columns) {
    var _arguments4 = arguments,
      _this4 = this;
    return _asyncToGenerator(function* () {
      var timeout = _arguments4.length > 2 && _arguments4[2] !== undefined ? _arguments4[2] : TableUtils.APPLY_TABLE_CHANGE_TIMEOUT_MS;
      var dh = _this4.dh;
      return TableUtils.executeAndWaitForEvent(t => t === null || t === void 0 ? void 0 : t.applyCustomColumns(columns), table, dh.Table.EVENT_CUSTOMCOLUMNSCHANGED, timeout);
    })();
  }

  /**
   * Apply filters to a given table.
   * @param table Table to apply filters to
   * @param filters Filters to apply
   * @param timeout Timeout before cancelling the promise that waits for the next
   * dh.Table.EVENT_FILTERCHANGED event
   * @returns a Promise to the Table that resolves after the next
   * dh.Table.EVENT_FILTERCHANGED event
   */
  applyFilter(table, filters) {
    var _arguments5 = arguments,
      _this5 = this;
    return _asyncToGenerator(function* () {
      var timeout = _arguments5.length > 2 && _arguments5[2] !== undefined ? _arguments5[2] : TableUtils.APPLY_TABLE_CHANGE_TIMEOUT_MS;
      var dh = _this5.dh;
      return TableUtils.executeAndWaitForEvent(
      // TODO: #21450 Update JS API to accept readonly arrays
      t => t === null || t === void 0 ? void 0 : t.applyFilter(filters), table, dh.Table.EVENT_FILTERCHANGED, timeout);
    })();
  }

  /**
   * Apply sorts to a given Table.
   * @param table The table to apply sorts to
   * @param sorts The sorts to apply
   * @param timeout Timeout before cancelling the promise that waits for the next
   * dh.Table.EVENT_SORTCHANGED event
   * @returns a Promise to the Table that resolves after the next
   * dh.Table.EVENT_SORTCHANGED event
   */
  applySort(table, sorts) {
    var _arguments6 = arguments,
      _this6 = this;
    return _asyncToGenerator(function* () {
      var timeout = _arguments6.length > 2 && _arguments6[2] !== undefined ? _arguments6[2] : TableUtils.APPLY_TABLE_CHANGE_TIMEOUT_MS;
      var dh = _this6.dh;
      return TableUtils.executeAndWaitForEvent(
      // TODO: #21450 Update JS API to accept readonly arrays
      t => t === null || t === void 0 ? void 0 : t.applySort(sorts), table, dh.Table.EVENT_SORTCHANGED, timeout);
    })();
  }

  /**
   * Create a filter condition that results in zero results for a given column
   * @param column
   */
  makeNeverFilter(column) {
    var dh = this.dh;
    var value = null;
    if (TableUtils.isTextType(column.type)) {
      // Use 'a' so that it can work for String or Character types
      value = dh.FilterValue.ofString('a');
    } else if (TableUtils.isBooleanType(column.type)) {
      value = dh.FilterValue.ofBoolean(true);
    } else if (TableUtils.isDateType(column.type)) {
      value = dh.FilterValue.ofNumber(dh.DateWrapper.ofJsDate(new Date()));
    } else {
      value = dh.FilterValue.ofNumber(0);
    }
    var eqFilter = column.filter().eq(value);
    var notEqFilter = column.filter().notEq(value);
    return eqFilter.and(notEqFilter);
  }

  /**
   * @param columnType The column type to make the filter value from.
   * @param value The value to make the filter value from.
   * @returns The FilterValue item for this column/value combination
   */
  makeFilterValue(columnType, value) {
    var dh = this.dh;
    var type = TableUtils.getBaseType(columnType);
    if (TableUtils.isTextType(type)) {
      return dh.FilterValue.ofString(value);
    }
    if (TableUtils.isLongType(type)) {
      return dh.FilterValue.ofNumber(dh.LongWrapper.ofString(TableUtils.removeCommas(value)));
    }
    return dh.FilterValue.ofNumber(TableUtils.removeCommas(value));
  }

  /**
   * Takes a value and converts it to an `dh.FilterValue`
   *
   * @param columnType The column type to make the filter value from.
   * @param value The value to actually set
   * @returns The FilterValue item for this column/value combination
   */
  makeFilterRawValue(columnType, rawValue) {
    var dh = this.dh;
    if (TableUtils.isCharType(columnType)) {
      return dh.FilterValue.ofString(typeof rawValue === 'number' ? String.fromCharCode(rawValue) : rawValue);
    }
    if (TableUtils.isTextType(columnType)) {
      return dh.FilterValue.ofString(rawValue);
    }
    if (TableUtils.isBooleanType(columnType)) {
      return dh.FilterValue.ofBoolean(rawValue);
    }
    return dh.FilterValue.ofNumber(rawValue);
  }

  /**
   * Creates an Eq filter for the given column and raw value
   * @param column The column to set the filter on
   * @param rawValue The nullable value to filter on
   * @returns The filter for this column/value combination
   */
  makeNullableEqFilter(column, rawValue) {
    if (rawValue == null) {
      return column.filter().isNull();
    }
    return column.filter().eq(this.makeFilterRawValue(column.type, rawValue));
  }

  /**
   * Converts a string value to a value appropriate for the column
   * @param columnType The column type to make the value for
   * @param text The string value to make a type for
   * @param timeZone The time zone to make this value in if it is a date type. E.g. America/New_York
   */
  makeValue(columnType, text, timeZone) {
    var dh = this.dh;
    if (text === 'null') {
      return null;
    }
    if (TableUtils.isTextType(columnType)) {
      return text;
    }
    if (TableUtils.isLongType(columnType)) {
      return dh.LongWrapper.ofString(TableUtils.removeCommas(text));
    }
    if (TableUtils.isBooleanType(columnType)) {
      return TableUtils.makeBooleanValue(text, true);
    }
    if (TableUtils.isDateType(columnType)) {
      var _DateUtils$parseDateR3 = DateUtils.parseDateRange(dh, text, timeZone),
        _DateUtils$parseDateR4 = _slicedToArray(_DateUtils$parseDateR3, 1),
        date = _DateUtils$parseDateR4[0];
      return date;
    }
    if (TableUtils.isNumberType(columnType)) {
      return TableUtils.makeNumberValue(text);
    }
    log.error('Unexpected column type', columnType);
    return null;
  }

  /**
   * Create a filter using the selected items
   * Has a flag for invertSelection as we start from a "Select All" state and a user just deselects items.
   * Since there may be millions of distinct items, it's easier to build an inverse filter.
   * @param column The column to set the filter on
   * @param selectedValues The values that are selected
   * @param invertSelection Invert the selection (eg. All items are selected, then you deselect items)
   * @returns Returns a `in` or `notIn` FilterCondition as necessary, or null if no filtering should be applied (everything selected)
   */
  makeSelectValueFilter(column, selectedValues, invertSelection) {
    var dh = this.dh;
    if (selectedValues.length === 0) {
      if (invertSelection) {
        // No filter means select everything
        return null;
      }

      // KLUDGE: Return a conflicting filter to show no results.
      // Could recognize this situation at a higher or lower level and pause updates on the
      // table, but this situation should be rare and that wouldn't be much gains for some added complexity
      return this.makeNeverFilter(column);
    }
    var values = [];
    var isNullSelected = false;
    for (var i = 0; i < selectedValues.length; i += 1) {
      var value = selectedValues[i];
      if (value == null) {
        isNullSelected = true;
      } else if (TableUtils.isTextType(column.type)) {
        values.push(dh.FilterValue.ofString(typeof value === 'number' ? String.fromCharCode(value) : value));
      } else if (TableUtils.isBooleanType(column.type)) {
        values.push(dh.FilterValue.ofBoolean(Boolean(value)));
      } else {
        values.push(dh.FilterValue.ofNumber(value));
      }
    }
    if (isNullSelected) {
      if (values.length > 0) {
        if (invertSelection) {
          return column.filter().isNull().not().and(column.filter().notIn(values));
        }
        return column.filter().isNull().or(column.filter().in(values));
      }
      if (invertSelection) {
        return column.filter().isNull().not();
      }
      return column.filter().isNull();
    }
    if (invertSelection) {
      return column.filter().notIn(values);
    }
    return column.filter().in(values);
  }
}
_TableUtils = TableUtils;
_defineProperty(TableUtils, "dataType", {
  BOOLEAN: 'boolean',
  CHAR: 'char',
  DATETIME: 'datetime',
  DECIMAL: 'decimal',
  INT: 'int',
  STRING: 'string',
  UNKNOWN: 'unknown'
});
_defineProperty(TableUtils, "sortDirection", {
  ascending: 'ASC',
  descending: 'DESC',
  reverse: 'REVERSE',
  none: null
});
_defineProperty(TableUtils, "APPLY_TABLE_CHANGE_TIMEOUT_MS", 30000);
_defineProperty(TableUtils, "REVERSE_TYPE", Object.freeze({
  NONE: 'none',
  PRE_SORT: 'pre-sort',
  POST_SORT: 'post-sort'
}));
// Regex looking for a negative or positive integer or decimal number
_defineProperty(TableUtils, "NUMBER_REGEX", /^-?\d+(\.\d+)?$/);
_defineProperty(TableUtils, "executeAndWaitForEvent", /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator(function* (exec, table, eventType) {
    var timeout = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : _TableUtils.APPLY_TABLE_CHANGE_TIMEOUT_MS;
    if (table == null) {
      return null;
    }
    var eventPromise = _TableUtils.makeCancelableTableEventPromise(table, eventType, timeout);
    exec(table);
    yield eventPromise;
    return table;
  });
  return function (_x, _x2, _x3) {
    return _ref2.apply(this, arguments);
  };
}());
export default TableUtils;
//# sourceMappingURL=TableUtils.js.map